import { useEffect, useState } from "react";
import {
	decodeShare,
	mergeReply,
	shareDifferences,
	sharedTripCopy,
	updateSharedTrip,
	voteKey,
	type SharePayload,
	type TripWorkspace,
} from "@japangolmok/core";
import { listTrips, saveTrip } from "../storage/trips";
import { Layout, usePageMeta } from "../ui/Shell";
import { CopyText, SendPayload } from "./ShareSheet";
import { track } from "../analytics/events";
export function SharePage() {
	const [payload, setPayload] = useState<SharePayload | null>(null),
		[error, setError] = useState(""),
		[local, setLocal] = useState<TripWorkspace | null>(null),
		[notice, setNotice] = useState(""),
		[before, setBefore] = useState<TripWorkspace | null>(null),
		[nick, setNick] = useState(""),
		[marks, setMarks] = useState<Record<string, "yes" | "skip">>({}),
		[busy, setBusy] = useState(false),
		[person, setPerson] = useState("");
	usePageMeta(
		"일행이 보낸 일본 여행 — 일본골목",
		"링크에 담긴 일정만 열어요. 가입이나 설치 없이 볼 수 있어요.",
	);
	useEffect(() => {
		let active = true;
		const code =
			window.location.hash ||
			new URLSearchParams(location.search).get("d") ||
			"";
		decodeShare(code)
			.then(async (p) => {
				const rows = await listTrips();
				if (!active) return;
				setPayload(p);
				if (p.kind === "trip") {
					setLocal(
						rows.find((t) => t.sharedFrom?.shareId === p.shareId) ?? null,
					);
					track("shared_trip_opened");
				} else if (p.kind === "reply")
					setLocal(rows.find((t) => t.shareId === p.shareId) ?? null);
				try {
					const old = sessionStorage.getItem(
						`jgm-reply:${p.kind === "trip" ? p.shareId : ""}`,
					);
					if (old && p.kind === "trip") {
						const v = JSON.parse(old);
						setMarks(v.marks ?? {});
						setNick(v.nick ?? "");
					}
				} catch {}
			})
			.catch(() => {
				if (active)
					setError(
						"링크 끝이 잘린 것 같아요. 보낸 분께 다시 보내 달라고 해 주세요. 압축 링크를 못 여는 브라우저라면 압축 없는 링크를 요청해 주세요.",
					);
			});
		return () => {
			active = false;
		};
	}, []);
	async function change(make: () => TripWorkspace) {
		if (busy) return;
		setBusy(true);
		try {
			const next = make();
			await saveTrip(next);
			setBefore(local ? structuredClone(local) : null);
			setLocal(next);
			setNotice("이 기기에 저장했어요.");
			if (payload?.kind === "trip") track("shared_trip_saved");
		} catch {
			setError(
				"저장하지 못했어요. 원본은 바꾸지 않았어요. 저장 공간을 확인하고 다시 눌러 주세요.",
			);
		} finally {
			setBusy(false);
		}
	}
	return (
		<Layout>
			<section className="section shared-trip">
				<a className="back-link" href="/trips">
					내 여행
				</a>
				<h1>일행이 보낸 여행</h1>
				{error && <div role="alert"><p>{error}</p>{!payload && <><a className="button primary" href="/trips">저장한 여행 열기</a> <a className="button secondary" href="/trips/new">새 여행 만들기</a></>}</div>}
				{!payload && !error && (
					<p role="status">링크의 내용을 여는 중이에요.</p>
				)}
				{payload?.kind === "trip" && (
					<>
						<h2>
							{payload.trip.destinationCity} · {payload.trip.startDate}–
							{payload.trip.endDate}
						</h2>
						<p>
							이 링크는 보낸 시점의 사본이에요. 바뀌면 새 링크를 받아 주세요.
						</p>
						{local ? (
							<>
								{local.sharedFrom!.revision >= payload.revision ? (
									<p>
										이미 저장한 여행이에요. 가지고 있는 사본이 같거나 더
										최신이에요.
									</p>
								) : (
									<>
										<h3>바뀐 점 보기</h3>
										<ul>
											{shareDifferences(local, payload.trip).map((x) => (
												<li key={x}>{x}</li>
											))}
										</ul>
										<button
											type="button"
											className="button primary"
											disabled={busy}
											onClick={() =>
												void change(() => updateSharedTrip(local, payload))
											}
										>
											내 사본 갱신하기
										</button>
									</>
								)}
								<a className="button secondary" href={`/trips/${local.id}`}>
									저장한 여행 열기
								</a>
							</>
						) : (
							<>
								<button
									type="button"
									className="button primary"
									disabled={busy}
									onClick={() => void change(() => sharedTripCopy(payload))}
								>
									내 여행으로 저장
								</button>
								{/KAKAOTALK/i.test(navigator.userAgent) && (
									<p>
										카톡 안 브라우저에 저장돼요. 현지에서 쓰려면 카톡 화면의 ⋯
										메뉴 → 다른 브라우저로 열기 뒤 다시 저장하거나 홈 화면에
										추가해 주세요.
									</p>
								)}
							</>
						)}
						{payload.trip.party && (
							<label>
								내 별명
								<select
									value={person}
									onChange={(e) => setPerson(e.target.value)}
								>
									<option value="">고르기</option>
									{payload.trip.party.map((p) => (
										<option key={p.id} value={p.id}>
											{p.nickname}
										</option>
									))}
								</select>
							</label>
						)}
						{person && (
							<p>
								내 담당{" "}
								{
									payload.trip.checklist.filter((c) => c.assignee === person)
										.length
								}
								개 ·{" "}
								{payload.trip.checklist
									.filter((c) => c.assignee === person)
									.map((c) => c.title)
									.join(" · ")}
							</p>
						)}
						{payload.trip.days.map((d, i) => (
							<article className="day-card" key={d.date}>
								<h3>
									{i + 1}일차 · {d.date}
								</h3>
								<ol>
									{d.items.map((item) => (
										<li key={item.id}>
											<strong>
												{item.time} {item.title}
											</strong>
											<div className="button-row">
												{(["yes", "skip"] as const).map((value) => (
													<button
														type="button"
														className="button secondary"
														aria-pressed={marks[voteKey(item.id)] === value}
														key={value}
														onClick={() => {
															const next = {
																...marks,
																[voteKey(item.id)]: value,
															};
															setMarks(next);
															try {
																sessionStorage.setItem(
																	`jgm-reply:${payload.shareId}`,
																	JSON.stringify({ marks: next, nick }),
																);
															} catch {}
														}}
													>
														{value === "yes" ? "꼭 가요" : "빼도 돼요"}
													</button>
												))}
											</div>
										</li>
									))}
								</ol>
							</article>
						))}
						{payload.trip.stay && (
							<p>
								숙소 · {payload.trip.stay.name} · {payload.trip.stay.addressJa}
							</p>
						)}
						<label>
							별명 · 실명 대신 부르는 이름
							<input
								maxLength={10}
								value={nick}
								onChange={(e) => setNick(e.target.value)}
							/>
						</label>
						<p>
							별명과 고른 표시가 링크에 담겨요. 받는 사람과 메신저가 볼 수
							있어요.
						</p>
						{Object.keys(marks).length && nick.trim() ? (
							<SendPayload
								payload={{
									kind: "reply",
									shareId: payload.shareId,
									nick: nick.trim(),
									marks: Object.entries(marks),
								}}
								text={`${nick}님의 여행 의견`}
							/>
						) : (
							<p>하나 이상 고르고 별명을 적으면 의견을 보낼 수 있어요.</p>
						)}
						<a href={`/trips/new?destination=${payload.trip.destination}`}>
							나도 여행 만들기
						</a>
					</>
				)}
				{payload?.kind === "reply" && (
					<>
						<h2>
							{payload.nick}님 의견 {payload.marks.length}개를 합칠까요?
						</h2>
						{local ? (
							<>
								<ul>
									{payload.marks.map(([key, value]) => (
										<li key={key}>
											{local.days
												.flatMap((d) => d.items)
												.find((i) => voteKey(i.id) === key)?.title ??
												"현재 일정에 없는 장소"}{" "}
											· {value === "yes" ? "꼭 가요" : "빼도 돼요"}
										</li>
									))}
								</ul>
								<button
									type="button"
									className="button primary"
									disabled={busy}
									onClick={() => void change(() => mergeReply(local, payload))}
								>
									의견 합치기
								</button>
								<a href={`/trips/${local.id}#itinerary`}>
									내 일정에서 의견 보기
								</a>
							</>
						) : (
							<>
								<p>
									이 브라우저에는 그 여행이 없어요. 여행을 만든 앱에서 받은 링크
									붙여 넣기로 넣어 주세요.
								</p>
								<CopyText text={location.href} label="답장 링크 복사" />
							</>
						)}
					</>
				)}
				{payload?.kind === "payment" && (
					<>
						<h2>일행 정산</h2>
						{payload.rows.map((r, i) => (
							<p key={`${r.from}:${r.to}`}>
								{r.from} → {r.to} · ¥{r.amountJpy.toLocaleString()}
							</p>
						))}
						<a href="/trips/new">나도 여행 만들기</a>
					</>
				)}
				{before && (
					<button
						type="button"
						className="button secondary"
						disabled={busy}
						onClick={async () => {
							try {
								await saveTrip(before);
								setLocal(before);
								setBefore(null);
								setNotice("합치기 전으로 되돌렸어요.");
							} catch {
								setError("되돌리기를 저장하지 못했어요. 다시 눌러 주세요.");
							}
						}}
					>
						합치기 되돌리기
					</button>
				)}
				<p role="status">{notice}</p>
				<a href="/">홈으로</a>
			</section>
		</Layout>
	);
}
